import { useMemo, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import useContactColumnsMetadataExtended from 'hooks/queries/useContactColumnsMetadataExtended';
import useContactEventColumnsMetadataExtended from 'hooks/queries/useContactEventColumnsMetadataExtended';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import MultiSelectDropdown, { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';
import PageLayout from 'domains/core/components/PageLayout';
import Table, { ColumnOption } from 'domains/core/components/Table';
import ToggleSelector, { ToggleSelectorOption } from 'domains/core/components/ToggleSelector';
import { themeColors } from 'colors';

type TableType = 'contact' | 'contact-event';

const toggleOptions: ToggleSelectorOption[] = [
    {
        value: 'contact',
        label: 'contact columns',
        ariaLabel: 'contact columns',
        testId: 'contact-columns-toggle',
    },
    {
        value: 'contact-event',
        label: 'contact event columns',
        ariaLabel: 'contact event columns',
        testId: 'contact-event-columns-toggle',
    },
];

const RECORD_TYPES_VISIBLE = 2;

const renderBooleanCell = (value: boolean) =>
    value ? (
        <CheckBoxIcon sx={{ color: themeColors.success.main, fontSize: 20 }} aria-label="yes" />
    ) : (
        <CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-label="no" />
    );

const renderRecordTypesCell = (value?: string[]) => {
    if (!value || value.length === 0) return '-';
    const visible = value.slice(0, RECORD_TYPES_VISIBLE).join(', ');
    const overflow = value.length - RECORD_TYPES_VISIBLE;
    if (overflow <= 0) return visible;
    return (
        <Tooltip title={value.join(', ')} placement="top" arrow>
            <span>
                {visible},{' '}
                <Typography
                    component="span"
                    sx={{ color: 'primary.main', cursor: 'default', textDecoration: 'underline dotted' }}
                >
                    +{overflow} more
                </Typography>
            </span>
        </Tooltip>
    );
};

const sortByStringOrEmpty = (rowA: any, rowB: any, columnId: string): number => {
    const a = rowA.values[columnId] || '';
    const b = rowB.values[columnId] || '';
    return a.localeCompare(b);
};

const CustomerSupportPage = () => {
    const [selectedTable, setSelectedTable] = useState<TableType>('contact');
    const [selectedRecordTypes, setSelectedRecordTypes] = useState<string[]>([]);

    const {
        data: contactColumns,
        isLoading: isLoadingContacts,
        error: contactError,
    } = useContactColumnsMetadataExtended({ recordTypes: selectedRecordTypes });

    const {
        data: contactEventColumns,
        isLoading: isLoadingEvents,
        error: eventError,
    } = useContactEventColumnsMetadataExtended({ recordTypes: selectedRecordTypes });

    const handleTableChange = (newTable: TableType) => {
        setSelectedTable(newTable);
        setSelectedRecordTypes([]);
    };

    // Derive options from the active table's unfiltered data.
    // We read from whichever dataset is loaded; when a filter is active the
    // options stay stable because React Query still holds the cached unfiltered result.
    const activeData = selectedTable === 'contact' ? contactColumns : contactEventColumns;
    const recordTypeOptions: MultiSelectOption<string>[] = useMemo(() => {
        if (!activeData) return [];
        const allTypes = new Set<string>();
        activeData.forEach((col) => col.recordTypes?.forEach((t) => allTypes.add(t)));
        return Array.from(allTypes)
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [activeData]);

    const tableColumns: ColumnOption[] = useMemo(
        () => [
            {
                Header: 'column name',
                accessor: 'columnName',
                align: 'left',
                sortType: 'string',
            },
            {
                Header: 'custom',
                accessor: 'isCustom',
                align: 'center',
                sortType: 'basic',
                Cell: ({ value }: { value: boolean }) => renderBooleanCell(value),
            },
            {
                Header: 'segmentable',
                accessor: 'isSegmentable',
                align: 'center',
                sortType: 'basic',
                Cell: ({ value }: { value: boolean }) => renderBooleanCell(value),
            },
            ...(selectedTable === 'contact'
                ? [
                      {
                          Header: 'merge tag',
                          accessor: 'isMergeTag',
                          align: 'center' as const,
                          sortType: 'basic' as const,
                          Cell: ({ value }: { value: boolean }) => renderBooleanCell(value),
                      },
                  ]
                : []),
            {
                Header: 'sql type',
                accessor: 'sqlType',
                align: 'left',
                sortType: 'string',
                Cell: ({ value }: { value?: string }) => value || '-',
            },
            {
                Header: 'record types',
                accessor: 'recordTypes',
                align: 'left',
                sortType: 'basic',
                Cell: ({ value }: { value?: string[] }) => renderRecordTypesCell(value),
            },
            ...(selectedTable === 'contact'
                ? [
                      {
                          Header: 'default value',
                          accessor: 'personalizationDefault',
                          align: 'left' as const,
                          sortType: sortByStringOrEmpty,
                          Cell: ({ value }: { value?: string }) => value || '-',
                      },
                      {
                          Header: 'preview value',
                          accessor: 'personalizationPreview',
                          align: 'left' as const,
                          sortType: sortByStringOrEmpty,
                          Cell: ({ value }: { value?: string }) => value || '-',
                      },
                  ]
                : []),
        ],
        [selectedTable]
    );

    const tableData = useMemo(() => {
        if (selectedTable === 'contact') {
            return contactColumns || [];
        }
        return contactEventColumns || [];
    }, [selectedTable, contactColumns, contactEventColumns]);

    const isLoading = selectedTable === 'contact' ? isLoadingContacts : isLoadingEvents;
    const error = contactError || eventError;

    const tableTitle = selectedTable === 'contact' ? 'contact columns' : 'contact event columns';

    const recordTypeFilter = recordTypeOptions.length > 0 && (
        <MultiSelectDropdown
            options={recordTypeOptions}
            selectedValues={selectedRecordTypes}
            onApply={setSelectedRecordTypes}
            label="Record Type"
            testIdPrefix="record-type-filter"
            menuId="record-type-filter-menu"
        />
    );

    if (error) {
        const errorMessage = error.message || 'Unknown error occurred';
        return (
            <>
                <PageLayout header="customer support" showSettingsHeader>
                    <Stack
                        width="100%"
                        height="100%"
                        spacing={3}
                        role="status"
                        aria-label="error loading customer support data"
                        data-testid="customer-support-error-container"
                    />
                </PageLayout>
                <ErrorSnackbar errorMessage={`failed to load metadata: ${errorMessage}`} />
            </>
        );
    }

    return (
        <PageLayout
            header="customer support"
            showSettingsHeader
            headerOthers={recordTypeFilter || undefined}
            data-testid="customer-support-page"
        >
            <Stack width="100%" height="100%" spacing={3} data-testid="customer-support-container">
                <Box data-testid="customer-support-content">
                    <ToggleSelector
                        value={selectedTable}
                        options={toggleOptions}
                        onChange={(newValue) => handleTableChange(newValue as TableType)}
                        title={tableTitle}
                        testId="table-type-toggle-selector"
                    />
                    <Box
                        data-testid={`${selectedTable}-metadata-table`}
                        role="region"
                        aria-label={`${tableTitle} metadata table`}
                    >
                        <Table columns={tableColumns} data={tableData} isLoading={isLoading} />
                    </Box>
                </Box>
            </Stack>
        </PageLayout>
    );
};

export default CustomerSupportPage;
